import React, {useEffect, useState} from "react";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {InputSwitch} from "primereact/inputswitch";
import {Editor} from "primereact/editor";
import {TabView, TabPanel} from "primereact/tabview";
import {Tag} from "primereact/tag";
import {AppContext} from "../../HomePage";
import {AnnouncementService} from "../../service/AnnouncementService";
import {Loading, IndexTemplate} from "../../utils/InlineComponents";
import {toDateTimeString} from "../../utils/TimeUtil";
import AdminPageHeader from "./AdminPageHeader";

const LANGUAGES = ['tr', 'en'];

const AnnouncementManagement = () => {

    const {t, toast, setError} = React.useContext(AppContext);
    const announcementService = new AnnouncementService();

    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rowData, setRowData] = useState(null);
    const [showFormModal, setShowFormModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [htmlMode, setHtmlMode] = useState(false);

    const newAnnouncement = () => ({id: null, title: {tr: '', en: ''}, body_html: {tr: '', en: ''}, is_published: false});

    useEffect(() => {
        loadAnnouncements();
    }, []);

    const loadAnnouncements = () => {
        setLoading(true);
        announcementService.adminList().then(items => {
            setAnnouncements(items);
            setLoading(false);
        }).catch(error => { setError(error); setError(null); setLoading(false); });
    };

    const saveAnnouncement = (announcement) => {
        announcementService.save(announcement).then(() => {
            setShowFormModal(false);
            setRowData(null);
            loadAnnouncements();
            toast.current.show({severity: 'success', summary: t('actions.save'), detail: t('message_detail.successful')});
        }).catch(error => { setError(error); setError(null); });
    };

    const deleteAnnouncement = () => {
        announcementService.remove(rowData.id).then(() => {
            setShowDeleteModal(false);
            setRowData(null);
            loadAnnouncements();
            toast.current.show({severity: 'success', summary: t('actions.delete'), detail: t('message_detail.successful')});
        }).catch(error => { setError(error); setError(null); setShowDeleteModal(false); });
    };

    const togglePublish = (announcement) => {
        saveAnnouncement({...announcement, is_published: !announcement.is_published});
    };

    const setLocalized = (field, language, value) => {
        setRowData({...rowData, [field]: {...rowData[field], [language]: value}});
    };

    const statusTemplate = (announcement) => announcement.is_published
        ? <Tag severity="success" value={t('announcement.published')}/>
        : <Tag severity="warning" value={t('announcement.draft')}/>;

    const dateTemplate = (announcement) => <span>{announcement.published_at ? toDateTimeString(announcement.published_at) : '-'}</span>;

    const actionTemplate = (announcement) => <React.Fragment>
        <Button icon="pi pi-pencil" className="p-button-text p-button-plain p-button-rounded"
                tooltip={t('actions.update')} tooltipOptions={{position: 'bottom'}}
                onClick={() => { setRowData({...newAnnouncement(), ...announcement}); setHtmlMode(false); setShowFormModal(true); }}/>
        <Button icon={announcement.is_published ? 'pi pi-eye-slash' : 'pi pi-arrow-circle-up'}
                className="p-button-text p-button-plain p-button-rounded"
                tooltip={announcement.is_published ? t('announcement.unpublish') : t('announcement.publish')}
                tooltipOptions={{position: 'bottom'}} onClick={() => togglePublish(announcement)}/>
        <Button icon="pi pi-trash" className="p-button-text p-button-danger p-button-rounded"
                tooltip={t('actions.delete')} tooltipOptions={{position: 'bottom'}}
                onClick={() => { setRowData(announcement); setShowDeleteModal(true); }}/>
    </React.Fragment>;

    const formBody = rowData && (
        <div className="p-fluid">
            <TabView>
                {LANGUAGES.map(language => (
                    <TabPanel key={language} header={t('announcement.language_' + language)}>
                        <div className="p-field">
                            <label htmlFor={'title_' + language}>{t('announcement.title')}</label>
                            <InputText id={'title_' + language} value={rowData.title[language] || ''}
                                       onChange={(e) => setLocalized('title', language, e.target.value)}/>
                        </div>
                        <div className="p-field">
                            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.25em'}}>
                                <label style={{margin: 0}}>{t('announcement.body')}</label>
                                <span style={{display: 'inline-flex', alignItems: 'center', gap: '.5em'}}>
                                    <label style={{margin: 0}}>{t('announcement.html_source')}</label>
                                    <InputSwitch checked={htmlMode} onChange={(e) => setHtmlMode(e.value)}/>
                                </span>
                            </div>
                            {htmlMode
                                ? <InputTextarea rows={12} value={rowData.body_html[language] || ''} style={{fontFamily: 'monospace'}}
                                                 onChange={(e) => setLocalized('body_html', language, e.target.value)}/>
                                : <Editor style={{height: '280px'}} value={rowData.body_html[language] || ''}
                                          onTextChange={(e) => setLocalized('body_html', language, e.htmlValue || '')}/>}
                        </div>
                    </TabPanel>
                ))}
            </TabView>
            <div className="p-field" style={{display: 'flex', alignItems: 'center', gap: '.6em', marginTop: '.5em'}}>
                <InputSwitch checked={rowData.is_published === true}
                             onChange={(e) => setRowData({...rowData, is_published: e.value})}/>
                <label style={{margin: 0}}>{t('announcement.published')}</label>
            </div>
        </div>
    );

    return <React.Fragment>

        <Dialog header={t('announcement.delete_confirm')} visible={showDeleteModal}
                style={{maxWidth: '50vw', minWidth: '20vw'}} breakpoints={{'960px': '75vw', '640px': '95vw'}}
                modal={true} onHide={() => { setShowDeleteModal(false); setRowData(null); }}
                footer={<div>
                    <Button label={t('yes_no.yes')} icon="pi pi-check" className="p-button-danger" onClick={deleteAnnouncement}/>
                    <Button label={t('yes_no.no')} icon="pi pi-times" className="p-button-warning" onClick={() => { setShowDeleteModal(false); setRowData(null); }}/>
                </div>}>
            {rowData && (rowData.title.tr || rowData.title.en)}
        </Dialog>

        <Dialog header={t('announcement.form')} visible={showFormModal}
                style={{width: '860px', maxWidth: '96vw'}} breakpoints={{'960px': '85vw', '640px': '95vw'}}
                modal={true} onHide={() => { setShowFormModal(false); setRowData(null); }}
                footer={<div>
                    <Button label={t('actions.cancel')} icon="pi pi-times" className="p-button-warning"
                            onClick={() => { setShowFormModal(false); setRowData(null); }}/>
                    <Button label={t('actions.save')} icon="pi pi-check" className="p-button-success"
                            onClick={() => saveAnnouncement(rowData)}/>
                </div>}>
            {formBody}
        </Dialog>

        <div className="card management-page">
            <AdminPageHeader
                title={t('menu_item.announcement_management')}
                subtitle={t('menu.management_hub.page.announcement_management')}
                actions={<Button label={t('actions.add')} icon="pi pi-plus" className="p-button-success"
                                 onClick={() => { setRowData(newAnnouncement()); setHtmlMode(false); setShowFormModal(true); }}/>}
            />
            {loading
                ? <div className="p-col-12" style={{textAlign: 'center'}}><Loading/></div>
                : <DataTable value={announcements} responsive={true} emptyMessage={t('announcement.empty')}>
                    <Column body={IndexTemplate} style={{width: '3em'}}/>
                    <Column field="title.tr" header={t('announcement.title')} body={(a) => a.title.tr || a.title.en}/>
                    <Column header={t('announcement.status')} body={statusTemplate} style={{width: '9em'}}/>
                    <Column field="published_at" header={t('announcement.published_at')} body={dateTemplate} style={{width: '13em'}}/>
                    <Column field="created_by" header={t('announcement.created_by')} style={{width: '16em'}}/>
                    <Column body={actionTemplate} style={{textAlign: 'center', width: '11em'}}/>
                </DataTable>}
        </div>
    </React.Fragment>;
};

export default AnnouncementManagement;
